import { useEffect, useState } from 'react';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import { Modal } from '@/components/Modal';
import { Button } from '@/components/Button';
import { FormField } from '@/components/FormField';
import { Input } from '@/components/Input';
import { Select } from '@/components/Select';
import { Textarea } from '@/components/Textarea';
import { useCompanyCurrency, useLabels } from '@/contexts/WorkspaceContext';
import { todayYmd } from '@/utils/date';
import {
  LEASE_PAYMENTS, Lease, LeasePayments, PropertyUnit, RENT_PERIODS, RentPeriod, realEstateService,
} from '@/services/real-estate.service';
import { ContactPicker, PickedContact, RENT_PERIOD_LABEL, UnitPicker, addDaysYmd, addYearsYmd, formatPrice, withArticle } from './shared';

/** Units a new lease can claim — the backend refuses sale-only, sold and already-leased ones. */
export const rentable = (u: PropertyUnit) => u.purpose !== 'sale' && u.status !== 'sold' && u.status !== 'rented' && !u.currentLeaseUid;

const oneYearFrom = (start: string) => addDaysYmd(addYearsYmd(start, 1), -1);

/** Same rounding as the backend's leaseMonths(): 1 Jan → 31 Dec is 12. */
export function leaseMonths(start: string, end: string): number {
  const s = new Date(`${start}T00:00:00Z`);
  const e = new Date(`${end}T00:00:00Z`);
  const months = (e.getUTCFullYear() - s.getUTCFullYear()) * 12 + (e.getUTCMonth() - s.getUTCMonth());
  const dayPart = (e.getUTCDate() - s.getUTCDate() + 1) / 30;
  return Math.max(1, Math.round(months + dayPart));
}

export const chequesLabel = (n: number) => `${n} ${n === 1 ? 'payment' : 'payments'}`;

function addMonthsYmd(ymd: string, months: number): string {
  const [y, m, d] = ymd.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1 + months, d)).toISOString().slice(0, 10);
}

const TERMS: Array<{ label: string; end: (start: string) => string }> = [
  { label: '6 months', end: (s) => addDaysYmd(addMonthsYmd(s, 6), -1) },
  { label: '1 year', end: oneYearFrom },
  { label: '2 years', end: (s) => addDaysYmd(addYearsYmd(s, 2), -1) },
];

interface Props {
  open: boolean;
  onClose: () => void;
  onSaved: (l: Lease) => void;
  /** Pre-picked unit (e.g. from the property page) — pass a value held in state. */
  unit?: PropertyUnit | null;
}

export default function LeaseFormModal({ open, onClose, onSaved, unit: presetUnit }: Props) {
  const t = useLabels();
  const companyCurrency = useCompanyCurrency();
  const [unit, setUnit] = useState<PropertyUnit | null>(null);
  const [tenant, setTenant] = useState<PickedContact | null>(null);
  const [start, setStart] = useState(todayYmd());
  const [end, setEnd] = useState(oneYearFrom(todayYmd()));
  const [endTouched, setEndTouched] = useState(false);
  const [rent, setRent] = useState('');
  const [period, setPeriod] = useState<RentPeriod>('yearly');
  const [payments, setPayments] = useState<LeasePayments>(1);
  const [deposit, setDeposit] = useState('');
  const [registration, setRegistration] = useState('');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    const today = todayYmd();
    setTenant(null);
    setStart(today);
    setEnd(oneYearFrom(today));
    setEndTouched(false);
    setPayments(1);
    setDeposit('');
    setRegistration('');
    setNotes('');
    const u = presetUnit ?? null;
    setUnit(u);
    setRent(u?.rentPrice !== null && u?.rentPrice !== undefined ? String(u.rentPrice) : '');
    setPeriod(u?.rentPeriod ?? 'yearly');
  }, [open, presetUnit]);

  /** Picking a unit brings its asking rent in; the agent can still change it. */
  const applyUnit = (u: PropertyUnit | null) => {
    setUnit(u);
    if (!u) return;
    if (u.rentPrice !== null && u.rentPrice !== undefined) setRent(String(u.rentPrice));
    setPeriod(u.rentPeriod ?? 'yearly');
  };

  const changeStart = (s: string) => {
    setStart(s);
    if (!endTouched && s) setEnd(oneYearFrom(s));
  };

  const currency = unit?.currency || companyCurrency;
  const rentNum = Number(rent);
  const validDates = !!start && !!end && end > start;
  const months = validDates ? leaseMonths(start, end) : 0;
  const total = rentNum > 0 && months ? (period === 'monthly' ? rentNum * months : rentNum * (months / 12)) : 0;
  const n = Math.max(1, Math.min(payments, months || 1));
  const each = total ? Math.floor((total / n) * 100) / 100 : 0;

  const save = async () => {
    if (!unit) { toast.error('Pick the unit being let'); return; }
    if (!tenant) { toast.error(`Pick the tenant (${withArticle(t('contacts', 'singular').toLowerCase())})`); return; }
    if (!validDates) { toast.error('The lease must end after it starts'); return; }
    if (!(rentNum >= 1)) { toast.error('Enter the rent'); return; }
    const dep = deposit.trim() ? Number(deposit) : undefined;
    if (dep !== undefined && !(dep >= 0)) { toast.error('The deposit must be 0 or more'); return; }
    setBusy(true);
    try {
      const saved = await realEstateService.createLease({
        unitUid: unit.unitUid,
        tenantContactUid: tenant.contactUid,
        startDate: start,
        endDate: end,
        rentAmount: rentNum,
        rentPeriod: period,
        payments,
        securityDeposit: dep,
        registrationNumber: registration.trim() || undefined,
        notes: notes.trim() || undefined,
      });
      toast.success(`${t('leases', 'singular')} created — ${saved.unit?.label ?? unit.label} is now rented`);
      onSaved(saved);
    } catch (e: unknown) {
      if (!(e as { response?: unknown })?.response) toast.error('Could not save — check your connection');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open={open} onClose={() => !busy && onClose()} title={`New ${t('leases', 'singular').toLowerCase()}`} widthClass="max-w-xl">
      <FormField label="Unit" required hint="Units for rent without an active lease">
        <UnitPicker value={unit} onChange={applyUnit} purpose="rent" accept={rentable} fallbackCurrency={companyCurrency} autoFocus={!presetUnit} />
      </FormField>
      <FormField label="Tenant" required hint={`Must be one of your ${t('contacts').toLowerCase()}`}>
        <ContactPicker value={tenant} onChange={setTenant} placeholder="Find the tenant by name or phone" />
      </FormField>

      <div className="grid grid-cols-2 gap-x-3">
        <FormField label="Starts" required>
          <Input type="date" value={start} onChange={(e) => changeStart(e.target.value)} />
        </FormField>
        <FormField label="Ends" required error={start && end && !validDates ? 'After the start date' : undefined}>
          <Input type="date" value={end} min={start || undefined} onChange={(e) => { setEnd(e.target.value); setEndTouched(true); }} invalid={!!start && !!end && !validDates} />
        </FormField>
      </div>
      <div className="-mt-2 mb-4 flex flex-wrap gap-1.5">
        {TERMS.map((x) => {
          const target = start ? x.end(start) : '';
          const on = !!target && target === end;
          return (
            <button
              key={x.label}
              type="button"
              disabled={!start}
              onClick={() => { setEnd(target); setEndTouched(true); }}
              className={clsx('rounded-full border px-2.5 py-1 text-2xs font-semibold transition-colors', on ? 'border-primary bg-primary-soft text-primary' : 'border-border text-text-secondary hover:border-border-strong')}
            >
              {x.label}
            </button>
          );
        })}
      </div>

      <div className="grid grid-cols-[minmax(0,1fr)_auto] gap-x-3">
        <FormField label="Rent" required>
          <Input type="number" inputMode="decimal" min={1} step="any" value={rent} onChange={(e) => setRent(e.target.value)} iconLeft={<span className="text-2xs font-semibold">{currency}</span>} />
        </FormField>
        <FormField label="Period">
          <Select value={period} onChange={(e) => setPeriod(e.target.value as RentPeriod)} className="h-10">
            {RENT_PERIODS.map((p) => <option key={p} value={p}>{RENT_PERIOD_LABEL[p]}</option>)}
          </Select>
        </FormField>
      </div>
      <div className="grid grid-cols-1 gap-x-3 sm:grid-cols-2">
        <FormField label="Payments (cheques)" hint="Per term">
          <Select value={String(payments)} onChange={(e) => setPayments(Number(e.target.value) as LeasePayments)}>
            {LEASE_PAYMENTS.map((p) => <option key={p} value={p}>{chequesLabel(p)}</option>)}
          </Select>
        </FormField>
        <FormField label="Security deposit">
          <Input type="number" inputMode="decimal" min={0} step="any" value={deposit} onChange={(e) => setDeposit(e.target.value)} iconLeft={<span className="text-2xs font-semibold">{currency}</span>} />
        </FormField>
      </div>
      {total > 0 && (
        <p className="-mt-1 mb-4 rounded-xl bg-surface-alt px-3 py-2 text-xs text-text-secondary">
          {months} {months === 1 ? 'month' : 'months'} · <span className="font-semibold text-text-primary">{formatPrice(Math.round(total * 100) / 100, currency)}</span> in total
          {n > 1 && <> · {n} payments of about {formatPrice(each, currency)}</>}
        </p>
      )}
      <FormField label="Registration no." hint="e.g. the Ejari / Tawtheeq number">
        <Input value={registration} onChange={(e) => setRegistration(e.target.value)} maxLength={60} />
      </FormField>
      <FormField label="Notes">
        <Textarea rows={2} maxLength={4000} value={notes} onChange={(e) => setNotes(e.target.value)} />
      </FormField>

      <div className="mt-2 flex justify-end gap-2 border-t border-border pt-4">
        <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant="primary" onClick={() => void save()} loading={busy}>Create {t('leases', 'singular').toLowerCase()}</Button>
      </div>
    </Modal>
  );
}
